import React, { useState, useEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import Modal from 'react-modal';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebaseConnection';
import Header from '../../components/Header';
import Title from '../../components/Title';
import { BsCalendar2MonthFill } from "react-icons/bs";
import './calendario.css'

Modal.setAppElement('#root'); // Definir o elemento raiz do aplicativo para acessibilidade

const Calendario = () => {
  const [events, setEvents] = useState([]);
  const [user, setUser] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [userData, setUserData] = useState(null);
  const [cidades, setCidades] = useState([]);
  const [igrejas, setIgrejas] = useState([]);
  const [selectedCidade, setSelectedCidade] = useState('');
  const [selectedIgreja, setSelectedIgreja] = useState('');

  const fetchIgrejaDetails = useCallback(async (igrejaId) => {
    if (!igrejaId) {
      console.error('ID da igreja é inválido:', igrejaId);
      return;
    }
    try {
      const igrejaDoc = await getDoc(doc(db, 'Igreja', igrejaId));
      if (igrejaDoc.exists()) {
        const igrejaData = igrejaDoc.data();

        setCategoryNames({
          atriu: igrejaData.atriu || 'Átrio',
          corredor: igrejaData.corredor || 'Pedidos de oração',
          som: igrejaData.som || 'Som',
          banheiro: igrejaData.banheiro || 'Sanitário',
          estacionamento: igrejaData.estacionamento || 'Estacionamento',
        });
      } else {
        console.error('Dados da igreja não encontrados.');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da igreja:', error);
    }
  }, []);




  const fetchUserData = useCallback(async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'User', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Buscar o nome da cidade
        const cidadeDoc = await getDoc(doc(db, 'Cidade', userData.cidade));
        if (cidadeDoc.exists()) {
          userData.cidadeNome = cidadeDoc.data().nome;
        } else {
          console.error('Cidade não encontrada.');
        }

        // Buscar o nome da igreja
        const igrejaDoc = await getDoc(doc(db, 'Igreja', userData.igreja));
        if (igrejaDoc.exists()) {
          userData.igrejaNome = igrejaDoc.data().nome;
        } else {
          console.error('Igreja não encontrada.');
        }

        // Buscar tipo de cargo (já existente no código)
        const cargoDoc = await getDoc(doc(db, 'Cargo', userData.cargo));
        if (cargoDoc.exists()) {
          userData.cargoTipo = cargoDoc.data().tipo;
        } else {
          console.error('Cargo não encontrado.');
        }

        return userData;
      } else {
        console.error('Usuário não encontrado.');
        return null;
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      return null;
    }
  }, []);


  const fetchReports = useCallback(async (cidade, igreja) => {
    try {
      const reportsQuery = query(
        collection(db, 'Reports'),
        where('ativo', '==', true),
        where('cidade', '==', cidade),
        where('igreja', '==', igreja)
      );

      const reportsSnapshot = await getDocs(reportsQuery);

      if (reportsSnapshot.empty) {
        return [];
      }

      const reports = [];

      reportsSnapshot.forEach((reportDoc) => {
        const reportData = reportDoc.data();
        reports.push(reportData);
      });

      return reports;
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
      return [];
    }
  }, []);

  const fetchAllCidades = useCallback(async () => {
    try {
      const cidadesSnapshot = await getDocs(collection(db, 'Cidade'));
      const allCidades = cidadesSnapshot.docs.map(doc => ({
        id: doc.id,
        nome: doc.data().nome,
      }));
      setCidades(allCidades);
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
    }
  }, []);

  const fetchIgrejasByCidade = useCallback(async (cidadeId) => {
    try {
      const igrejasQuery = query(
        collection(db, 'Igreja'),
        where('cidade', '==', cidadeId)
      );
      const igrejasSnapshot = await getDocs(igrejasQuery);
      const allIgrejas = igrejasSnapshot.docs.map(doc => ({
        id: doc.id,
        nome: doc.data().nome,
      }));
      setIgrejas(allIgrejas);
    } catch (error) {
      console.error('Erro ao buscar igrejas:', error);
    }
  }, []);

  const fetchEventsForUser = useCallback(async (cidade, igreja) => {
    const reports = await fetchReports(cidade, igreja);

    const userEvents = [];

    reports.forEach((reportData) => {
      reportData.report.forEach((item) => {
        if (item.date) {
          const adjustedDate = new Date(item.date);
          adjustedDate.setHours(adjustedDate.getHours() + 3); // Ajusta o fuso horário

          const existingEvent = userEvents.find(
            (event) => event.date.getTime() === adjustedDate.getTime()
          );

          if (existingEvent) {
            ['atriu', 'banheiro', 'corredor', 'estacionamento', 'som'].forEach((local) => {
              if (item[local]) {
                existingEvent.details[local] = existingEvent.details[local] || [];
                existingEvent.details[local].push(...item[local]);
              }
            });
          } else {
            userEvents.push({
              title: `Culto ${adjustedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
              date: adjustedDate,
              details: { ...item },
            });
          }
        }
      });
    });

    // Ordena os eventos pela hora
    userEvents.sort((a, b) => a.date - b.date);

    setEvents(userEvents);
  }, [fetchReports]);


  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('@userLogado'));
    if (storedUser) {
      setUser(storedUser);
      fetchUserData(storedUser.uid).then((userData) => {
        if (userData) {
          setUserData(userData);

          if (userData.cargoTipo === 'Admin') {
            fetchAllCidades();
            setSelectedCidade(userData.cidade);
            fetchIgrejasByCidade(userData.cidade).then(() => {
              setSelectedIgreja(userData.igreja);
              fetchEventsForUser(userData.cidade, userData.igreja);
              fetchIgrejaDetails(userData.igreja); // Adicione esta linha
            });
          } else {
            setSelectedCidade(userData.cidade);
            setSelectedIgreja(userData.igreja);
            fetchEventsForUser(userData.cidade, userData.igreja);
            fetchIgrejaDetails(userData.igreja);
          }
        }
      });
    } else {
      console.error('Nenhum usuário encontrado no localStorage.');
    }
  }, [fetchUserData, fetchEventsForUser, fetchAllCidades, fetchIgrejasByCidade, fetchIgrejaDetails]);


  const handleCidadeChange = async (event) => {
    const cidadeId = event.target.value;
    setSelectedCidade(cidadeId);
    await fetchIgrejasByCidade(cidadeId);
  };

  const handleIgrejaChange = (event) => {
    const igrejaId = event.target.value;
    setSelectedIgreja(igrejaId);
    fetchEventsForUser(selectedCidade, igrejaId);
  };

  const renderEvents = (date) => {
    const dayEvents = events.filter(
      (event) => event.date.toDateString() === date.toDateString()
    );

    return dayEvents.map((event, index) => (
      <div
        key={index}
        onClick={() => handleEventClick(event)}
        className='title-card'
      >
        <strong>{event.title}</strong>
      </div>
    ));
  };

  const adjustTimezone = (date) => {
    if (!date) return { date: 'Data não disponível', time: 'Hora não disponível' };

    // Verificar se a data é um Timestamp do Firebase (Firestore)
    if (date.seconds) {
      const firebaseDate = new Date(date.seconds * 1000);
      firebaseDate.setHours(firebaseDate.getHours() + 3); // Ajuste para UTC+3

      const formattedDate = firebaseDate.toLocaleString('pt-BR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      const formattedTime = firebaseDate.toLocaleString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

      return { date: formattedDate, time: formattedTime };
    }

    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + 3);

    const formattedDate = newDate.toLocaleString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const formattedTime = newDate.toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    return { date: formattedDate, time: formattedTime };
  };

  const [categoryNames, setCategoryNames] = useState({
    atriu: '',
    corredor: '',
    som: '',
    banheiro: '',
    estacionamento: '',
  });


  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleEventClick = (event) => {
    setSelectedEvents([event]);
    setModalIsOpen(true);
  };


  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedEvents([]);
  };

  useEffect(() => {
    if (selectedIgreja) { // Verifica se selectedIgreja não é nulo, vazio ou undefined
      fetchIgrejaDetails(selectedIgreja);
    }
  }, [selectedIgreja, fetchIgrejaDetails]);





  return (
    <div>
      <Header />
      <div className="content">
        <Title name="Calendário">
          <BsCalendar2MonthFill size={25} />
        </Title>
        <div className="containerCalendar">
          {userData && (userData.cargoTipo === 'UserP' || userData.cargoTipo === 'UserS') && (
            <div className='cabecalho_Calendar'>
              <label><strong>Cidade: </strong> {userData.cidadeNome}</label>
              <label><strong>Casa de Oração: </strong> {userData.igrejaNome}</label>
            </div>
          )}
          {userData && userData.cargoTipo === 'Admin' && (
            <div className='cabecalho_Calendar'>
              <label>
                <strong>Cidade:</strong>
                <select onChange={handleCidadeChange} value={selectedCidade}>
                  <option value="">Selecione uma cidade</option>
                  {cidades.map(cidade => (
                    <option key={cidade.id} value={cidade.id}>{cidade.nome}</option>
                  ))}
                </select>
              </label>
              <label>
                <strong>Casa de Oração:</strong>
                <select onChange={handleIgrejaChange} value={selectedIgreja} disabled={!selectedCidade}>
                  <option value="">Selecione uma Casa de Oração</option>
                  {igrejas.map(igreja => (
                    <option key={igreja.id} value={igreja.id}>{igreja.nome}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <div className="calendar-container" style={{ overflowX: 'auto', padding: '1rem' }}>
            <Calendar
              tileContent={({ date, view }) => view === 'month' && renderEvents(date)}
              locale="pt-BR"
            />
          </div>
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            onAfterOpen={scrollToTop}
            contentLabel="Event Details"
            style={{
              overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
              content: {
                maxWidth: '600px',
                margin: 'auto',
                borderRadius: '10px',
                padding: '20px',
                border: 'none',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Detalhes do Culto</h2>
            {selectedEvents.length > 0 && (
              <div>
                {selectedEvents.map((task, index) => {
                  const { date } = adjustTimezone(task.date);
                  return (
                    <li key={index} style={{ listStyle: 'none' }}>
                      <p><strong>Data:</strong> {date}</p>
                      <p><strong>Hora:</strong> {selectedEvents[0].date.toLocaleTimeString()}</p>
                    </li>
                  );
                })}
                <br></br>
                {Object.keys(categoryNames).map((local) => {
                  const pessoasFeminino = selectedEvents[0].details[local]?.filter(person => person.genero === 'feminino') || [];
                  const pessoasMasculino = selectedEvents[0].details[local]?.filter(person => person.genero === 'masculino') || [];

                  return (
                    <div key={local}>
                      <br></br>
                      {(pessoasFeminino.length > 0 || pessoasMasculino.length > 0) && (
                        <h3>{categoryNames[local]}:</h3>
                      )}

                      {/* Feminino */}
                      {pessoasFeminino.length > 0 && (
                        <>
                          <br></br>
                          <p>Irmãs:</p>
                          {pessoasFeminino.map((person, index) => (
                            <div
                              key={`fem-${index}`}
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                padding: '10px',
                                marginBottom: '10px',
                                backgroundColor: '#f9f9f9',
                              }}
                            >
                              <p><strong>Nome:</strong> {person.name}</p>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Masculino */}
                      {pessoasMasculino.length > 0 && (
                        <>
                          <br></br>
                          <p>Irmãos:</p>
                          {pessoasMasculino.map((person, index) => (
                            <div
                              key={`masc-${index}`}
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                padding: '10px',
                                marginBottom: '10px',
                                backgroundColor: '#f9f9f9',
                              }}
                            >
                              <p><strong>Nome:</strong> {person.name}</p>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <button
              onClick={closeModal}
              style={{
                display: 'block',
                margin: '20px auto 0',
                backgroundColor: '#007BFF',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                padding: '10px 20px',
                cursor: 'pointer',
              }}
            >
              Fechar
            </button>
          </Modal>

        </div>
      </div>
    </div>
  );
};

export default Calendario;
